import { useState, useMemo, useContext, Dispatch, SetStateAction } from 'react';
import { t } from '@lingui/core/macro';
import noop from 'lodash/noop';
import get from 'lodash/get';
import UWEFormContext from '../context/UWEFormContext';
import DataRemarksApi, { DataRemark, DataRemarkMessage } from '../api/DataRemarksApi';
import { WFAccessToken } from '../api/types';
import startsWith from 'lodash/startsWith';
import { last, sortBy } from 'lodash';
import { AuthContextValue } from '../context/AuthContext';

type CategoryFilter = 'Cross-Schedule' | 'Red Flag' | 'Manual' | 'Amendment' | 'Modification Required';

export const isRemarkResponded = (remark: DataRemark, auth: AuthContextValue, mode: string) => {
  if (!remark) return false;
  const userId = auth?.user?.user?.id;
  const { messages } = remark;

  if (messages.length === 0) return false;

  // Don't trust the server's return order for "last" — sort defensively so a message that
  // happens to come back out of chronological order can't flip the responded/active bucket.
  const lastMessage = last(sortBy(messages, m => new Date(m.createdAt).getTime()));

  // Institution
  if (mode.includes('response')) {
    return lastMessage != null && lastMessage?.createdBy?.id == userId;
  }
  // Analyst
  return lastMessage?.createdBy?.id !== userId;
};

export const categoryKey = (category: string): CategoryFilter => {
  if (category.includes('Red Flag')) return 'Red Flag';
  if (category.includes('Cross-Schedule')) return 'Cross-Schedule';
  if (category.includes('Amendment')) return 'Amendment';
  if (category.includes('Modification Required')) return 'Modification Required';
  return 'Manual';
};

export const getRemarkCategoryLabel = (category: string): string => {
  const normalizedCategory = category ?? '';
  if (normalizedCategory.includes('Red Flag')) return t`Red Flag`;
  if (normalizedCategory.includes('Cross-Schedule')) return t`Cross-Schedule`;
  if (normalizedCategory.includes('Amendment')) return t`Amendment`;
  if (normalizedCategory.includes('Modification Required')) return t`Modification Required`;
  return t`Manual`;
};

/** Schedule key for general remarks, which are attached to the entity rather than to a form field. */
export const OTHER_SCHEDULE_KEY = '__other__';

export const isGeneralRemark = (remark: Pick<DataRemark, 'fieldPath'>): boolean => {
  const paths = Array.isArray(remark.fieldPath) ? remark.fieldPath : [remark.fieldPath];
  return !paths.some(Boolean);
};

export const getScheduleKeysForRemark = (remark: DataRemark, subForms: Record<string, any>): string[] => {
  if (isGeneralRemark(remark)) return [OTHER_SCHEDULE_KEY];
  const paths = Array.isArray(remark.fieldPath) ? remark.fieldPath : [remark.fieldPath];
  const matched = new Set<string>();
  for (const path of paths) {
    for (const [key, sf] of Object.entries(subForms)) {
      const sfPath = sf?.path as string;
      if (!sfPath) continue;
      if (path === sfPath || path.startsWith(sfPath + '.')) {
        matched.add(key);
      }
    }
  }
  return Array.from(matched);
};

export type ScheduleOption = { value: string; label: string };

export type NewDataRemark = {
  fieldName: string;
  fieldPath: string;
  fieldDescription: string | null;
  fieldTitle: string;
  message: string;
  description: string;
  _fields: Array<{ name: string; path: string; value: any }>;
  /** Picked "Other" as the schedule: the remark is about the entity as a whole and carries no field. */
  isGeneral: boolean;
};

export type DataRemarkPermissions = {
  canAddRemarks: boolean;
  canEditRemarks: boolean;
  canResolveRemarks: boolean;
  canRespondToRemarks: boolean;
};

type UseDataRemarksOptions = {
  entity: { id: number } | null | undefined;
  auth: AuthContextValue;
  wfAccessToken: WFAccessToken;
  formSchema: Record<string, any>;
  mode?: string;
  readOnly?: boolean;
};

const EMPTY_REMARK: NewDataRemark = {
  fieldName: '',
  fieldPath: '',
  fieldDescription: null,
  fieldTitle: '',
  message: '',
  description: '',
  _fields: [],
  isGeneral: false,
};

export type DataRemarkWith<ExtraProps> = DataRemark & ExtraProps;

type RemarkField = { name: string; path: string; value: any };
/** Legacy remarks store a single scalar field path, which yields a single field rather than a list. */
type DataRemarkWithFields = DataRemarkWith<{ _fields?: RemarkField[] | RemarkField }>;

const NO_REMARKS: DataRemark[] = [];
const NO_SUBFORMS: Record<string, any> = {};

const withFields = (dr: DataRemark): DataRemarkWithFields => ({
  ...dr,
  _fields: Array.isArray(dr.fieldPath)
    ? dr.fieldPath.map((path, ix) => ({
        path,
        name: Array.isArray(dr.fieldName) ? dr.fieldName[ix] : dr.fieldName,
        value: Array.isArray(dr.fieldValue) ? dr.fieldValue[ix] : dr.fieldValue,
      }))
    : (({ path: dr.fieldPath, name: dr.fieldName, value: dr.fieldValue } as unknown) as RemarkField),
});

export const getFieldValue = (path: string, entity: any, formSchema: Record<string, any>, subforms: Record<string, any>): any => {
  let dataPath = '';
  if (startsWith(path, 'data.')) {
    dataPath = path;
    return get(entity, dataPath) || null;
  }

  // Remarks now store the actual data path directly (matching Cross-Schedule/Red Flag/Amendment
  // remarks), so try that first before falling back to the schema-tree walk below (only still
  // needed for remarks created before this change, which stored a schema-tree path instead).
  const directValue = get(entity, path);
  if (directValue !== undefined) {
    return directValue;
  }

  const splitPath = path.split('.');
  let targetSchema: Record<string, any> = formSchema;
  for (let i = 0; i < splitPath.length; i++) {
    const currentKey = splitPath[i];
    if (targetSchema.properties != null && targetSchema.properties[currentKey] != null) {
      targetSchema = targetSchema.properties[currentKey];
    } else if (targetSchema[currentKey] != null) {
      targetSchema = targetSchema[currentKey];
    }

    if (targetSchema['ui:field'] === 'SubformField' && targetSchema['ui:subformName'] != null) {
      const subform = subforms[targetSchema['ui:subformName']];
      if (!subform) return null;
      targetSchema = subform.schema;
      dataPath = subform['map:field'];
    }
  }

  dataPath = `${dataPath ? `${dataPath}.` : ''}${targetSchema['map:field']}`;
  return get(entity, dataPath) || null;
};

export function useDataRemarks({ entity, auth, wfAccessToken, formSchema, mode, readOnly }: UseDataRemarksOptions) {
  const uweFormContext = useContext(UWEFormContext);
  const subforms = (uweFormContext?.subForms || NO_SUBFORMS) as Record<string, any>;
  const sourceRemarks = uweFormContext?.dataRemarks || NO_REMARKS;
  const dataRemarks = useMemo(() => sourceRemarks.map(withFields), [sourceRemarks]);

  const setDataRemarks: Dispatch<SetStateAction<DataRemark[]>> = uweFormContext?.setDataRemarks ?? noop;
  const navigateToField: (fieldId: string, color?: string) => void = uweFormContext?.navigateToField ?? noop;
  const availableFieldsByPath = uweFormContext?.formFieldsByPath || {};
  const availableFieldsByDataPath = (uweFormContext as any)?.formFieldsByDataPath || {};

  const [selectedRemark, setSelectedRemark] = useState<DataRemark | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [newRemark, setNewRemark] = useState<NewDataRemark>(EMPTY_REMARK);
  const [editingRemark, setEditingRemark] = useState<DataRemark | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [errorCreatingRemark, setErrorCreatingRemark] = useState<any>(null);

  const [remarkToResolve, setRemarkToResolve] = useState<DataRemark | null>(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [errorResolvingRemark, setErrorResolvingRemark] = useState<any>(null);

  const isReviewMode = mode?.includes('review') || false;
  const isResponseMode = mode?.includes('response') || false;

  const permissions: DataRemarkPermissions = {
    canAddRemarks: isReviewMode && !readOnly,
    canEditRemarks: isReviewMode && !readOnly,
    canResolveRemarks: isReviewMode && !readOnly,
    canRespondToRemarks: (isResponseMode && !readOnly) || isReviewMode,
  };

  const availableSchedules: ScheduleOption[] = useMemo(() => {
    const keysWithRemarks = new Set<string>();
    for (const dr of dataRemarks) {
      getScheduleKeysForRemark(dr, subforms).forEach(k => keysWithRemarks.add(k));
    }
    const schedules = Object.entries(subforms)
      .filter(([key]) => keysWithRemarks.has(key))
      .map(([key, sf]) => ({ value: key, label: (sf?.title as string) || key }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (keysWithRemarks.has(OTHER_SCHEDULE_KEY)) schedules.push({ value: OTHER_SCHEDULE_KEY, label: t`Other` });
    return schedules;
  }, [dataRemarks, subforms]);

  /** General remarks send empty field lists: there is no field, value or path to record. */
  const remarkFieldLists = () => {
    if (newRemark.isGeneral) return { fieldName: [], fieldPath: [], fieldValue: [] };
    const fieldValue = getFieldValue(newRemark.fieldPath, entity, formSchema, subforms);
    return {
      fieldName: [newRemark.fieldName],
      fieldPath: [newRemark.fieldPath],
      fieldValue: [fieldValue ? JSON.stringify(fieldValue) : '--'],
    };
  };

  const isRemarkIncomplete = () => (!newRemark.isGeneral && !newRemark.fieldPath) || !newRemark.message;

  // ─── Create / Edit modal ──────────────────────────────────────────────────

  const openCreateModal = () => setShowCreateModal(true);

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingRemark(null);
    setNewRemark(EMPTY_REMARK);
    setErrorCreatingRemark(null);
  };

  const handleCreateRemark = async () => {
    if (isRemarkIncomplete()) {
      setErrorCreatingRemark(new Error(t`El campo y el mensaje son requeridos`));
      return;
    }
    setLoading(true);
    setErrorCreatingRemark(null);
    try {
      const created = await DataRemarksApi.createDataRemark({
        remark: {
          uweEntityId: entity?.id!,
          ...remarkFieldLists(),
          message: newRemark.message,
          description: newRemark.description || '',
          category: mode?.includes('amendment') ? 'Modification Required' : 'Manual',
          createdById: auth?.user?.user?.id,
        } as any,
        wfAccessToken,
      });
      setDataRemarks(prev => [created, ...prev]);
      handleModalClose();
    } catch (err) {
      console.error('Error creating data remark:', err);
      setErrorCreatingRemark(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRemark = (remarkId: number) => {
    const remark = dataRemarks.find(r => r.id === remarkId);
    if (remark) handleRemarkClick(remark);
  };

  const handleUpdateRemark = async () => {
    if (isRemarkIncomplete()) {
      setErrorCreatingRemark(new Error(t`El campo y el mensaje son requeridos`));
      return;
    }
    setLoading(true);
    setErrorCreatingRemark(null);
    try {
      const updated = await DataRemarksApi.updateDataRemark({
        id: editingRemark!.id!,
        remark: {
          ...remarkFieldLists(),
          message: newRemark.message,
          description: newRemark.description,
          category: editingRemark!.category,
        } as any,
        wfAccessToken,
      });
      setDataRemarks(prev => prev.map(r => (r.id === editingRemark!.id ? updated : r)));
      handleModalClose();
    } catch (err) {
      console.error('Error updating data remark:', err);
      setErrorCreatingRemark(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemarkClick = (remark: DataRemark) => {
    setNewRemark({
      fieldName: Array.isArray(remark.fieldName) ? remark.fieldName[0] ?? '' : remark.fieldName,
      fieldPath: Array.isArray(remark.fieldPath) ? remark.fieldPath[0] ?? '' : remark.fieldPath,
      fieldDescription: null,
      fieldTitle: '',
      message: remark.message,
      description: remark.description || '',
      _fields: remark._fields,
      isGeneral: isGeneralRemark(remark),
    });
    setEditingRemark(remark);
    setShowCreateModal(true);
  };

  // ─── Resolve ──────────────────────────────────────────────────────────────

  const handleResolveRemark = (remarkId: number) => {
    const remark = dataRemarks.find(r => r.id === remarkId);
    if (!remark) return;
    setErrorResolvingRemark(null);
    setRemarkToResolve(remark);
    setShowResolveModal(true);
  };

  /** Keeps the modal open (and mounted, so the typed notes survive) until the server accepts the resolution. */
  const handleSubmitResolution = async ({ resolutionNotes }: { resolutionNotes: string }) => {
    if (!remarkToResolve) return;
    setModalLoading(true);
    setErrorResolvingRemark(null);
    try {
      const updated = await DataRemarksApi.resolveDataRemark({
        id: remarkToResolve.id!,
        resolutionNotes: resolutionNotes || '',
        resolvedByUserId: auth?.user?.user?.id!,
        wfAccessToken,
      });
      setDataRemarks(prev => prev.map(r => (r.id === remarkToResolve.id ? updated : r)));
      setShowResolveModal(false);
      setRemarkToResolve(null);
    } catch (err) {
      console.error('Error resolving data remark:', err);
      setErrorResolvingRemark(err);
    } finally {
      setModalLoading(false);
    }
  };

  // ─── Add message (generic, by remark id) ─────────────────────────────────

  const handleAddMessageToRemark = async (
    remarkId: number,
    { message, file }: { message: string; file?: File | null }
  ): Promise<DataRemarkMessage> => {
    setModalLoading(true);
    try {
      const newMessage = await DataRemarksApi.addDataRemarkMessage({
        id: remarkId,
        message,
        file: file ?? undefined,
        wfAccessToken,
      });
      setDataRemarks(prev => prev.map(r => (r.id === remarkId ? { ...r, messages: [...(r.messages ?? []), newMessage] } : r)));
      setEditingRemark(prev => (prev?.id === remarkId ? { ...prev, messages: [...(prev.messages ?? []), newMessage] } : prev));
      return newMessage;
    } catch (err) {
      console.error('Error adding message to remark:', err);
      throw err;
    } finally {
      setModalLoading(false);
    }
  };

  // ─── Add message to editing remark ───────────────────────────────────────

  const handleAddMessageToEditingRemark = ({ message, file }: { message: string; file?: File | null }): Promise<DataRemarkMessage> | undefined => {
    if (!editingRemark) return;
    setErrorCreatingRemark(null);
    return handleAddMessageToRemark(editingRemark.id!, { message, file }).catch(err => {
      setErrorCreatingRemark(err);
      throw err;
    });
  };

  // ─── Bulk operations ──────────────────────────────────────────────────────

  const handleBulkResolve = async (ids: number[], resolutionNotes: string): Promise<{ resolved: number; skipped: number }> => {
    const result = await DataRemarksApi.bulkResolve({ ids, resolutionNotes, wfAccessToken });
    if (result.results?.length) {
      const resultMap = new Map(result.results.map((r: any) => [r.id, r]));
      setDataRemarks(prev => prev.map(r => (resultMap.has(r.id) ? (resultMap.get(r.id) as DataRemark) : r)));
    }
    return { resolved: result.resolved, skipped: result.skipped };
  };

  const handleBulkComment = async (
    ids: number[],
    message: string,
    attachmentId?: number
  ): Promise<{ commented: number; skipped: number }> => {
    const result = await DataRemarksApi.bulkComment({ ids, message, attachmentId, wfAccessToken });
    if (result.results?.length) {
      for (const msg of result.results) {
        setDataRemarks(prev => prev.map(r => (r.id === msg.dataRemarkId ? { ...r, messages: [...(r.messages ?? []), msg] } : r)));
      }
    }
    return { commented: result.commented, skipped: result.skipped };
  };

  return {
    // data
    dataRemarks,
    subforms,
    availableSchedules,
    // field navigation
    navigateToField,
    availableFieldsByPath,
    availableFieldsByDataPath,
    // accordion: the remark currently expanded
    selectedRemark,
    setSelectedRemark,
    // permissions
    permissions,
    // loading
    loading,
    modalLoading,
    // create / edit modal
    showCreateModal,
    openCreateModal,
    newRemark,
    setNewRemark,
    editingRemark,
    handleModalClose,
    handleCreateRemark,
    handleUpdateRemark,
    handleEditRemark,
    handleRemarkClick,
    handleAddMessageToRemark,
    handleAddMessageToEditingRemark,
    errorCreatingRemark,
    // resolve modal
    showResolveModal,
    setShowResolveModal,
    remarkToResolve,
    setRemarkToResolve,
    handleResolveRemark,
    handleSubmitResolution,
    errorResolvingRemark,
    // bulk
    handleBulkResolve,
    handleBulkComment,
    getFieldValueForPath: (path: string) => getFieldValue(path, entity, formSchema, subforms),
  };
}
